import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import { useRouter } from '@tanstack/react-router'

import { NewJobModal } from '@/components/NewJobModal'
import { formatPocketBaseError } from '@/lib/pocketbaseErrors'
import type { NewJobInitialValues } from '@/screens/NewJobScreen'
import { useDb } from '@/lib/dbContext'

interface NewJobModalContextValue {
  openNewJobModal: () => void
}

const NewJobModalContext = createContext<NewJobModalContextValue | null>(null)

export function useNewJobModal() {
  const ctx = useContext(NewJobModalContext)
  if (!ctx) {
    throw new Error('useNewJobModal must be used within NewJobModalProvider')
  }
  return ctx
}

export function NewJobModalProvider({ children }: { children: ReactNode }) {
  const db = useDb()
  const router = useRouter()
  const [isOpen, setIsOpen] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const openNewJobModal = useCallback(() => {
    setErrorMessage(null)
    setIsOpen(true)
  }, [])

  const value = useMemo(() => ({ openNewJobModal }), [openNewJobModal])

  return (
    <NewJobModalContext.Provider value={value}>
      {children}
      <NewJobModal
        open={isOpen}
        onOpenChange={(open) => {
          setIsOpen(open)
          if (!open) setErrorMessage(null)
        }}
        errorMessage={errorMessage}
        onSave={async (values: NewJobInitialValues) => {
          setErrorMessage(null)
          try {
            const job = await db.createJobAndWait?.(values)
            setIsOpen(false)
            if (job?.id) {
              router.navigate({ to: '/jobs/$id', params: { id: job.id } })
            }
          } catch (error) {
            setErrorMessage(
              formatPocketBaseError(error, {
                fallbackMessage: 'Unable to save job. Please try again.',
                fieldLabels: {
                  customer_id: 'Customer',
                  device_type: 'Type',
                  device_description: 'Device(s) description',
                  device_password: 'Device password',
                  fault: 'Fault',
                  peripherals: 'Peripherals',
                },
              }),
            )
          }
        }}
      />
    </NewJobModalContext.Provider>
  )
}
